import { z } from 'zod';
import { RuntimeType } from './gameManifest.js';

/**
 * GameSpec - The input the Creator Agent turns into a GameArtifact.
 *
 * Mirrors the structural contract in agents/creator/src/CreatorAgent.ts but
 * expressed as a Zod schema so the LLM Gateway / QA layer can validate
 * machine-generated specs before spending tokens.
 */
export const GameSpecSchema = z.object({
  template: z.string().min(1).describe('Template id, e.g. "reaction" | "clicker" | "memory"'),
  theme: z.string().min(1).describe('Visual theme, e.g. "space" | "ocean" | "forest"'),
  runtime: RuntimeType.optional().describe('Game runtime: "phaser" for action/reaction, "dom" for clicker/UI/card'),
  duration: z.number().positive().optional().describe('Expected duration in seconds'),
  difficulty: z.enum(['easy', 'medium', 'hard', 'expert']).optional(),
  mechanics: z.array(z.string()).optional().describe('Allowed mechanic tags, e.g. ["tap","timer"]'),
  monetization: z.enum(['none', 'ads', 'iap']).optional().default('none'),
});

export type GameSpec = z.infer<typeof GameSpecSchema>;

/**
 * GameArtifactMetadata - Provenance block attached to every artifact.
 */
export const GameArtifactMetadataSchema = z.object({
  template: z.string().min(1),
  theme: z.string().min(1),
  generated_at: z.string().datetime(),
  creator_version: z.string().min(1),
  estimated_duration: z.number().min(0),
});

export type GameArtifactMetadata = z.infer<typeof GameArtifactMetadataSchema>;

/**
 * GameArtifact - The structured unit the Creator Agent emits.
 *
 * This is the EXACT JSON shape the LLM is instructed to output. Every field
 * must be present and valid for the build step to even start.
 */
export const GameArtifactSchema = z.object({
  /** GameManifest-compatible object (loose record, validated downstream). */
  manifest: z.record(z.unknown()),
  /** Generated TypeScript source code. Must implement the GameRuntime interface. */
  source: z.string().min(1),
  /** Game configuration object validated against the template's config schema. */
  config: z.record(z.unknown()),
  /** Generated Vitest test source code. */
  tests: z.string().min(1),
  /** Provenance / bookkeeping. */
  metadata: GameArtifactMetadataSchema,
});

export type GameArtifact = z.infer<typeof GameArtifactSchema>;

/**
 * Narrower input used when repairing an existing artifact: only the mutable
 * pieces are expected back from the LLM.
 */
export const RepairedGameArtifactSchema = GameArtifactSchema.partial({
  metadata: true,
}).extend({
  source: z.string().min(1),
});

export type RepairedGameArtifact = z.infer<typeof RepairedGameArtifactSchema>;
